/**
 * HR Form Field Renderer — reusable field rendering for HR request forms.
 *
 * Extracted from HrNewRequestDrawer to enable:
 * - Full edit form in HrRequestDetailDrawer (correction workflow)
 * - Consistent field rendering across create & edit flows
 *
 * Supports all FIELD_TYPEs: DATE, TIME, TEXT, TEXTAREA, SELECT,
 * USER_PICKER, NUMBER, CHECKBOX, FILE, IBAN, INFO
 */

import React from "react";
import { FIELD_TYPE } from "../constants/hrRequestTypes";
import { DatePicker, DateTimePicker, TimePicker } from "../components/common/DatePicker";
import { DepartmentMemberPicker } from "../components/common/DepartmentMemberPicker";
import MultiSelectCombo from "../components/common/MultiSelectCombo/MultiSelectCombo";
import OrgEntityPicker from "../components/common/OrgEntityPicker/OrgEntityPicker";
import { czyPoleZastepstwa } from "./hrBrakOsoby";
import { FiUpload, FiPlus, FiTrash2 } from "react-icons/fi";
import fieldStyles from "./hrFormFieldRenderer.module.css";

import PrzewijanieTabeli from "../components/common/PrzewijanieTabeli";
// ── Work-days calculator (skip weekends) ─────────────────────────
export function calcWorkDays(from, to) {
    if (!from || !to) return 0;
    const d1 = new Date(from + "T00:00:00");
    const d2 = new Date(to + "T00:00:00");
    if (isNaN(d1.getTime()) || isNaN(d2.getTime()) || d2 < d1) return 0;
    let count = 0;
    const cur = new Date(d1);
    while (cur <= d2) {
        const dow = cur.getDay();
        if (dow !== 0 && dow !== 6) count++;
        cur.setDate(cur.getDate() + 1);
    }
    return count;
}

// ── Compute auto-calculated fields ───────────────────────────────
export function computeFields(fields, formData) {
    const updated = { ...formData };
    let changed = false;

    for (const field of fields) {
        if (!field.computed) continue;

        // workDays: auto-calculate from dateFrom/dateTo
        if (field.name === "workDays" && updated.dateFrom && updated.dateTo) {
            const days = calcWorkDays(updated.dateFrom, updated.dateTo);
            if (updated.workDays !== days) {
                updated.workDays = days;
                changed = true;
            }
        }
    }

    return changed ? updated : formData;
}

// ── [R24, polecenie właściciela 2026-09-13] Widoczność pola: showIf + showIfValue ──────────────
// „Pojazd prywatny — numer rejestracyjny i pojemność silnika” miało się pokazywać TYLKO przy samochodzie
// prywatnym. Seed delegacji niesie `showIf: "srodekTransportu", showIfValue: "prywatny"`, ale renderer znał
// wyłącznie `showIf` jako PRAWDZIWOŚĆ — więc pola pojazdu wyskakiwały przy KAŻDYM wybranym środku transportu
// (kolej, samolot…). Reguła:
//   • bez `showIf`                     → pole widoczne zawsze,
//   • `showIf` bez `showIfValue`       → jak dotąd: widoczne, gdy formData[showIf] jest prawdziwe,
//   • `showIf` + `showIfValue`         → widoczne, gdy formData[showIf] RÓWNA SIĘ wartości (porównanie napisów,
//                                        bo select oddaje napis, a JSON typu może trzymać liczbę/bool),
//   • `showIfValue` jako tablica       → wystarczy równość z KTÓRĄKOLWIEK; gdy formData[showIf] jest tablicą
//                                        (wybór wielokrotny) — wystarczy wspólny element.
// Jedno źródło dla renderera, walidacji, kreatora własnego i kreatora „w imieniu pracownika”.
export function isFieldVisible(field, formData) {
    if (!field || !field.showIf) return true;
    const aktualna = formData?.[field.showIf];
    if (field.showIfValue === undefined || field.showIfValue === null) return !!aktualna;
    const oczekiwane = (Array.isArray(field.showIfValue) ? field.showIfValue : [field.showIfValue]).map((w) => String(w));
    const wybrane = Array.isArray(aktualna) ? aktualna : [aktualna];
    return wybrane.some((w) => w !== undefined && w !== null && oczekiwane.includes(String(w)));
}

// [R24] Dane do wysyłki BEZ wartości pól, których człowiek nie widzi. Pracownik wybrał „samochód prywatny”, wpisał
// pojazd, a potem przełączył na „kolej” — pola zniknęły, ale ich wartości zostały w formData i szły na polecenie
// wyjazdu (PDF z pojazdem przy podróży koleją, klasa pojazdu przepisana do rozliczenia). Pętla do skutku: pole
// zależne od pola ukrytego też znika. Wejście NIE jest mutowane; brak zmian → ten sam obiekt.
export function bezUkrytychPol(fields, formData) {
    if (!Array.isArray(fields) || fields.length === 0 || !formData) return formData;
    let wynik = formData;
    for (let przebieg = 0; przebieg <= fields.length; przebieg++) {
        let zmiana = false;
        for (const f of fields) {
            if (!f?.name || !f.showIf || !Object.prototype.hasOwnProperty.call(wynik, f.name)) continue;
            if (isFieldVisible(f, wynik)) continue;
            if (wynik === formData) wynik = { ...formData };
            delete wynik[f.name];
            if (f.type === FIELD_TYPE.MONEY) delete wynik[`${f.name}Waluta`];
            zmiana = true;
        }
        if (!zmiana) break;
    }
    return wynik;
}

// ── Validate a single field ──────────────────────────────────────
export function validateField(field, value, formData) {
    // Don't validate hidden fields (showIf / showIfValue — patrz isFieldVisible)
    if (!isFieldVisible(field, formData)) {
        return { valid: true, error: null };
    }
    // INFO fields don't need validation
    if (field.type === FIELD_TYPE.INFO) {
        return { valid: true, error: null };
    }
    // [PAKIET 2026-09-02] WYBÓR WIELOKROTNY trzyma TABLICĘ, więc „pusto" to pusta tablica, a nie "" ani false.
    // Bez tej gałęzi pole obowiązkowe z pustym zaznaczeniem przechodziłoby walidację (`[] !== ""`),
    // a wniosek szedłby dalej bez odpowiedzi, której formularz wymagał.
    if (field.type === FIELD_TYPE.MULTISELECT) {
        if (field.required && (!Array.isArray(value) || value.length === 0)) {
            return { valid: false, error: `Pole "${field.label}" jest wymagane` };
        }
        return { valid: true, error: null };
    }

    // [PAKIET 2026-09-02] WYBÓR SPÓŁKI / DZIAŁU trzyma OBIEKT { id, name } — obiekt bez `id` (np. wynik
    // wyczyszczenia listy) jest wartością pustą, choć `value !== ""` byłoby prawdą.
    if (field.type === FIELD_TYPE.COMPANY_PICKER || field.type === FIELD_TYPE.DEPARTMENT_PICKER) {
        const pusty = value === null || value === undefined || value === ""
            || (typeof value === "object" && (value.id === null || value.id === undefined || value.id === ""));
        if (field.required && pusty) {
            return { valid: false, error: `Pole "${field.label}" jest wymagane` };
        }
        return { valid: true, error: null };
    }

    // [DELEGACJE 2026-09-12] TABELA POZYCJI trzyma TABLICĘ WIERSZY — obowiązkowa = co najmniej jeden wiersz,
    // a każdy wiersz musi mieć wypełnione kolumny oznaczone jako wymagane.
    if (field.type === FIELD_TYPE.REPEATER) {
        const wiersze = Array.isArray(value) ? value : [];
        if (field.required && wiersze.length === 0) {
            return { valid: false, error: `Tabela "${field.label}" musi mieć co najmniej jedną pozycję` };
        }
        const wymagane = (field.columns || []).filter((c) => c?.required);
        for (let i = 0; i < wiersze.length; i++) {
            for (const k of wymagane) {
                const v = wiersze[i]?.[k.name];
                if (v === null || v === undefined || v === "") {
                    return { valid: false, error: `Pozycja ${i + 1} w "${field.label}": uzupełnij "${k.label || k.name}"` };
                }
            }
        }
        return { valid: true, error: null };
    }

    // KWOTA: liczba (może być 0 tylko gdy pole nie jest obowiązkowe); tekst nieliczbowy to błąd, nie „pusto".
    if (field.type === FIELD_TYPE.MONEY) {
        const pusta = value === null || value === undefined || value === "";
        if (!pusta && !Number.isFinite(Number(value))) {
            return { valid: false, error: `Pole "${field.label}" musi być kwotą` };
        }
        if (field.required && !field.computed && pusta) {
            return { valid: false, error: `Pole "${field.label}" jest wymagane` };
        }
        return { valid: true, error: null };
    }

    // Required check
    if (field.required && !field.computed) {
        if (value === null || value === undefined || value === "" || value === false) {
            return { valid: false, error: `Pole "${field.label}" jest wymagane` };
        }
    }
    return { valid: true, error: null };
}

// ── Validate all fields ──────────────────────────────────────────
export function validateAllFields(fields, formData) {
    const errors = {};
    let isValid = true;
    for (const field of fields) {
        const result = validateField(field, formData[field.name], formData);
        if (!result.valid) {
            errors[field.name] = result.error;
            isValid = false;
        }
    }
    return { isValid, errors };
}

// ── Render a single form field ───────────────────────────────────
export function renderFormField(field, formData, onChange, options = {}) {
    const {
        user = null,
        readOnly = false,
        errors = {},
        styleOverrides = {},
    } = options;

    const value = formData[field.name];
    const error = errors[field.name];

    const handleChange = (name, val) => {
        onChange(name, val);
    };

    // showIf / showIfValue — conditional visibility (R24: pola pojazdu tylko przy „samochód prywatny”)
    if (!isFieldVisible(field, formData)) {
        return null;
    }

    // HIDDEN — pole techniczne, nigdy nie pokazywane człowiekowi.
    // BYŁO ŹLE: renderer nie miał gałęzi dla FIELD_TYPE.HIDDEN, więc pole spadało na koniec funkcji
    // i renderowało się jako ZWYKŁY, WIDOCZNY input tekstowy. HrNewRequestDrawer.js:791 pomija je
    // poprawnie — brakowało tej samej reguły tutaj, czyli w ścieżce korekty (HrRequestDetailDrawer).
    // SKUTEK DLA UŻYTKOWNIKA: poprawiając odesłany wniosek (np. wycofanie), pracownik widział obce
    // pole techniczne w rodzaju „ID wniosku" z identyfikatorem w środku — mógł je skasować albo
    // nadpisać, a validateField wymaga jego wartości, więc wniosek przestawał się dać wysłać.
    // NAPRAWA: ta sama reguła co w kreatorze — pole ukryte nie ma reprezentacji wizualnej.
    // Wartość zostaje w formData, więc wysyłka wniosku działa dokładnie jak dotąd.
    if (field.type === FIELD_TYPE.HIDDEN) {
        return null;
    }

    // INFO — read-only display block
    if (field.type === FIELD_TYPE.INFO) {
        return (
            <div key={field.name || "info"} className={fieldStyles.infoBox}>
                {field.content}
            </div>
        );
    }

    // CHECKBOX
    if (field.type === FIELD_TYPE.CHECKBOX) {
        return (
            <div key={field.name} className={fieldStyles.checkboxRow}>
                <input
                    type="checkbox"
                    className={fieldStyles.checkboxInput}
                    checked={!!value}
                    onChange={(e) => handleChange(field.name, e.target.checked)}
                    id={`edit-field-${field.name}`}
                    disabled={readOnly}
                />
                <label className={fieldStyles.checkboxLabel} htmlFor={`edit-field-${field.name}`}>
                    {field.label}
                </label>
            </div>
        );
    }

    // SELECT
    if (field.type === FIELD_TYPE.SELECT) {
        return (
            <div key={field.name} className={fieldStyles.fieldGroup}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                {field.description && <small className={fieldStyles.fieldHint}>{field.description}</small>}
                <select
                    className={`${fieldStyles.fieldSelect} ${error ? fieldStyles.fieldError : ""}`}
                    value={value || ""}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    disabled={readOnly}
                >
                    <option value="">Wybierz...</option>
                    {(field.options || []).map((opt, oi) => {
                        const o = typeof opt === "string" ? { value: opt, label: opt } : opt; // tolerancja starych zapisów
                        return <option key={o.value ?? oi} value={o.value}>{o.label ?? o.value}</option>;
                    })}
                </select>
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // TEXTAREA
    if (field.type === FIELD_TYPE.TEXTAREA) {
        return (
            <div key={field.name} className={fieldStyles.fieldGroup}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                {field.description && <small className={fieldStyles.fieldHint}>{field.description}</small>}
                <textarea
                    className={`${fieldStyles.fieldTextarea} ${error ? fieldStyles.fieldError : ""}`}
                    value={value || ""}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    placeholder={field.placeholder || `Wpisz ${field.label.toLowerCase()}...`}
                    readOnly={readOnly}
                />
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // DATE
    if (field.type === FIELD_TYPE.DATE) {
        return (
            <div key={field.name} className={fieldStyles.fieldGroup}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                {/* [R22] Czerwona ramka błędu na przycisku pickera — jak w DATETIME (opakowanie .pickerError). */}
                <div className={error ? fieldStyles.pickerError : undefined}>
                    <DatePicker
                        value={value || ""}
                        onChange={(val) => handleChange(field.name, val)}
                        placeholder="Wybierz datę"
                        clearable={!field.required}
                        disabled={readOnly}
                        ariaLabel={field.label}
                    />
                </div>
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // TIME
    if (field.type === FIELD_TYPE.TIME) {
        return (
            <div key={field.name} className={fieldStyles.fieldGroup}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                <div className={error ? fieldStyles.pickerError : undefined}>
                    <TimePicker
                        value={value || ""}
                        onChange={(val) => handleChange(field.name, val)}
                        disabled={readOnly}
                        ariaLabel={field.label}
                    />
                </div>
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // NUMBER
    if (field.type === FIELD_TYPE.NUMBER) {
        return (
            <div key={field.name} className={fieldStyles.fieldGroup}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && !field.computed && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                <input
                    type="number"
                    className={`${fieldStyles.fieldInput} ${field.readonly || field.computed ? fieldStyles.fieldInputReadonly : ""} ${error ? fieldStyles.fieldError : ""}`}
                    value={value ?? ""}
                    onChange={(e) => handleChange(field.name, e.target.value ? Number(e.target.value) : "")}
                    readOnly={field.readonly || field.computed || readOnly}
                    min={0}
                    placeholder={field.readonly || field.computed ? "(wyliczane automatycznie)" : "0"}
                />
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // ── [DELEGACJE 2026-09-12, doc 37 §4.5] DATA I GODZINA — jeden moment, nie dwa rozłączne pola ──
    // [R22, polecenie właściciela 2026-09-13] „wypełniając wnioski delegacji dodaj data picker oraz time picker
    // z aplikacji, nie ma tego". Stało tu natywne `<input type="datetime-local">` — kontrolka przeglądarki,
    // inna w każdej z nich, bez polskich nazw miesięcy i poza design systemem. Teraz to ten sam DateTimePicker,
    // którego używa okno zgłoszenia i ManagerRequestDrawer. WARTOŚĆ SIĘ NIE ZMIENIA: picker oddaje dokładnie
    // "YYYY-MM-DDTHH:mm" (to, co dawał natywny input), a wejście dalej obcinamy do minut — serwer i silnik diet
    // widzą ten sam napis co wczoraj. Atrybut `data-pole-datetime` zostaje na opakowaniu (zapadki i style).
    if (field.type === FIELD_TYPE.DATETIME) {
        return (
            <div key={field.name} className={fieldStyles.fieldGroup}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                {field.description && <small className={fieldStyles.fieldHint}>{field.description}</small>}
                <div data-pole-datetime={field.name} className={error ? fieldStyles.pickerError : undefined}>
                    <DateTimePicker
                        value={typeof value === "string" ? value.slice(0, 16) : ""}
                        onChange={(val) => handleChange(field.name, val)}
                        placeholder="Wybierz datę i godzinę"
                        clearable={!field.required}
                        disabled={readOnly}
                        ariaLabel={field.label}
                    />
                </div>
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // ── KWOTA Z WALUTĄ: wartość to liczba; waluta w `${name}Waluta`, gdy pole ma listę walut ──
    if (field.type === FIELD_TYPE.MONEY) {
        const waluty = Array.isArray(field.currencies) && field.currencies.length > 0 ? field.currencies : null;
        const klucz = `${field.name}Waluta`;
        return (
            <div key={field.name} className={fieldStyles.fieldGroup}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && !field.computed && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                {field.description && <small className={fieldStyles.fieldHint}>{field.description}</small>}
                <div className={fieldStyles.moneyRow}>
                    <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min={0}
                        className={`${fieldStyles.fieldInput} ${field.readonly || field.computed ? fieldStyles.fieldInputReadonly : ""} ${error ? fieldStyles.fieldError : ""}`}
                        value={value ?? ""}
                        onChange={(e) => handleChange(field.name, e.target.value === "" ? "" : Number(e.target.value))}
                        readOnly={field.readonly || field.computed || readOnly}
                        placeholder="0,00"
                        data-pole-money={field.name}
                    />
                    {waluty ? (
                        <select
                            className={fieldStyles.moneyCurrency}
                            value={formData[klucz] || waluty[0]}
                            onChange={(e) => handleChange(klucz, e.target.value)}
                            disabled={readOnly}
                            aria-label={`Waluta: ${field.label}`}
                        >
                            {waluty.map((w) => <option key={w} value={w}>{w}</option>)}
                        </select>
                    ) : (
                        <span className={fieldStyles.moneyCurrency}>PLN</span>
                    )}
                </div>
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // ── TABELA POZYCJI: N wierszy o kolumnach z definicji pola; wartość = tablica obiektów ──
    if (field.type === FIELD_TYPE.REPEATER) {
        const kolumny = Array.isArray(field.columns) ? field.columns.filter((c) => c?.name) : [];
        const wiersze = Array.isArray(value) ? value : [];
        const zapisz = (nowe) => handleChange(field.name, nowe);
        const zmienKomorke = (ri, nazwa, v) => zapisz(wiersze.map((w, i) => (i === ri ? { ...w, [nazwa]: v } : w)));
        const dodaj = () => zapisz([...wiersze, Object.fromEntries(kolumny.map((c) => [c.name, c.type === "checkbox" ? false : ""]))]);
        const usun = (ri) => zapisz(wiersze.filter((_, i) => i !== ri));
        const komorka = (c, w, ri) => {
            const v = w?.[c.name];
            const wspolne = { className: fieldStyles.repeaterInput, disabled: readOnly, "data-kolumna": c.name };
            switch (c.type) {
                case "number":
                case "money":
                    return <input type="number" step={c.type === "money" ? "0.01" : "any"} inputMode="decimal" value={v ?? ""} onChange={(e) => zmienKomorke(ri, c.name, e.target.value === "" ? "" : Number(e.target.value))} {...wspolne} />;
                // [R22] Komórki daty i daty z godziną: pickery z aplikacji zamiast natywnych kontrolek; napisy
                // w wierszu zostają takie same ("YYYY-MM-DD" / "YYYY-MM-DDTHH:mm"). `data-kolumna` siedzi na
                // opakowaniu, bo picker nie przepuszcza obcych atrybutów na swój przycisk.
                case "date":
                    return (
                        <div className={fieldStyles.repeaterPicker} data-kolumna={c.name}>
                            <DatePicker value={v || ""} onChange={(val) => zmienKomorke(ri, c.name, val)} disabled={readOnly} ariaLabel={c.label || c.name} placeholder="Wybierz datę" />
                        </div>
                    );
                case "datetime":
                    return (
                        <div className={fieldStyles.repeaterPicker} data-kolumna={c.name}>
                            <DateTimePicker value={typeof v === "string" ? v.slice(0, 16) : ""} onChange={(val) => zmienKomorke(ri, c.name, val)} disabled={readOnly} ariaLabel={c.label || c.name} placeholder="Data i godzina" />
                        </div>
                    );
                case "checkbox":
                    return <input type="checkbox" checked={!!v} onChange={(e) => zmienKomorke(ri, c.name, e.target.checked)} disabled={readOnly} data-kolumna={c.name} />;
                case "select":
                    return (
                        <select value={v ?? ""} onChange={(e) => zmienKomorke(ri, c.name, e.target.value)} {...wspolne}>
                            <option value="">—</option>
                            {(c.options || []).map((o, oi) => {
                                const op = typeof o === "string" ? { value: o, label: o } : o;
                                return <option key={op.value ?? oi} value={op.value}>{op.label ?? op.value}</option>;
                            })}
                        </select>
                    );
                default:
                    return <input type="text" value={v ?? ""} onChange={(e) => zmienKomorke(ri, c.name, e.target.value)} {...wspolne} />;
            }
        };
        return (
            <div key={field.name} className={fieldStyles.fieldGroup} data-pole-repeater={field.name}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                {field.description && <small className={fieldStyles.fieldHint}>{field.description}</small>}
                {kolumny.length === 0 ? (
                    <small className={fieldStyles.fieldHint}>To pole nie ma zdefiniowanych kolumn — poproś kadry o uzupełnienie definicji w konfiguratorze HR.</small>
                ) : (
                    <div className={fieldStyles.repeaterWrap}>
                        <PrzewijanieTabeli />
                        <table className={fieldStyles.repeaterTable}>
                            <thead>
                                <tr>
                                    <th className={fieldStyles.repeaterLp}>Lp.</th>
                                    {kolumny.map((c) => <th key={c.name}>{c.label || c.name}{c.required && <span className={fieldStyles.fieldRequired}>*</span>}</th>)}
                                    {!readOnly && <th className={fieldStyles.repeaterAkcje} aria-label="Akcje" />}
                                </tr>
                            </thead>
                            <tbody>
                                {wiersze.length === 0 && (
                                    <tr><td colSpan={kolumny.length + 2} className={fieldStyles.repeaterPusto}>Brak pozycji — dodaj pierwszą przyciskiem poniżej.</td></tr>
                                )}
                                {wiersze.map((w, ri) => (
                                    <tr key={ri} data-wiersz={ri}>
                                        <td className={fieldStyles.repeaterLp}>{ri + 1}</td>
                                        {kolumny.map((c) => <td key={c.name}>{komorka(c, w, ri)}</td>)}
                                        {!readOnly && (
                                            <td className={fieldStyles.repeaterAkcje}>
                                                <button type="button" className={fieldStyles.repeaterDel} onClick={() => usun(ri)} title="Usuń pozycję" aria-label={`Usuń pozycję ${ri + 1}`}>
                                                    <FiTrash2 size={13} />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!readOnly && (
                            <button type="button" className={fieldStyles.repeaterAdd} onClick={dodaj} data-dodaj-pozycje>
                                <FiPlus size={13} /> Dodaj pozycję
                            </button>
                        )}
                    </div>
                )}
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // FILE — in edit mode we show readonly reference (re-upload not supported for correction)
    if (field.type === FIELD_TYPE.FILE) {
        if (readOnly || options.isCorrection) {
            // Show file name if present, otherwise placeholder
            return (
                <div key={field.name} className={fieldStyles.fieldGroup}>
                    <label className={fieldStyles.fieldLabel}>
                        {field.label}
                    </label>
                    <div className={fieldStyles.fileReadonly}>
                        <FiUpload size={13} />
                        <span>{value?.name || (typeof value === "string" ? value : "Brak pliku")}</span>
                    </div>
                </div>
            );
        }
        return (
            <div key={field.name} className={fieldStyles.fieldGroup}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                <label className={fieldStyles.fileLabel}>
                    <FiUpload size={14} />
                    <span>{value?.name || "Wybierz plik..."}</span>
                    <input
                        type="file"
                        className={fieldStyles.fileInput}
                        onChange={(e) => handleChange(field.name, e.target.files?.[0] || null)}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                </label>
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // [PAKIET 2026-09-02] WYBÓR SPÓŁKI / DZIAŁU — wartość złożona { id, name }, patrz OrgEntityPicker.
    if (field.type === FIELD_TYPE.COMPANY_PICKER || field.type === FIELD_TYPE.DEPARTMENT_PICKER) {
        return (
            <div key={field.name} className={fieldStyles.fieldGroup}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                {field.description && <small className={fieldStyles.fieldHint}>{field.description}</small>}
                <OrgEntityPicker
                    kind={field.type === FIELD_TYPE.COMPANY_PICKER ? "company" : "department"}
                    value={value ?? null}
                    onChange={(val) => handleChange(field.name, val)}
                    disabled={readOnly || field.readonly}
                    className={fieldStyles.fieldSelect}
                    errorClassName={fieldStyles.fieldError}
                    hasError={!!error}
                />
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // [PAKIET 2026-09-02] WYBÓR WIELOKROTNY — wartość to TABLICA wartości opcji (np. ["ecm","vpn"]).
    // Świadomie na gotowym MultiSelectCombo, a nie na natywnym <select multiple>: ten drugi na dotyku
    // i przy dłuższych listach jest praktycznie nieobsługiwalny, a lista dostępów do systemów ma
    // kilkanaście pozycji.
    if (field.type === FIELD_TYPE.MULTISELECT) {
        const opcje = (field.options || []).map((opt) =>
            (typeof opt === "string" ? { value: opt, label: opt } : { value: opt.value, label: opt.label ?? opt.value }));
        return (
            <div key={field.name} className={fieldStyles.fieldGroup}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                {field.description && <small className={fieldStyles.fieldHint}>{field.description}</small>}
                <MultiSelectCombo
                    options={opcje}
                    values={Array.isArray(value) ? value : []}
                    onChange={(vals) => handleChange(field.name, vals)}
                    placeholder={field.placeholder || "— wybierz —"}
                    disabled={readOnly || field.readonly}
                />
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // USER_PICKER
    if (field.type === FIELD_TYPE.USER_PICKER) {
        return (
            <div key={field.name} className={fieldStyles.fieldGroup}>
                <label className={fieldStyles.fieldLabel}>
                    {field.label}
                    {field.required && <span className={fieldStyles.fieldRequired}>*</span>}
                </label>
                {/* [polecenie właściciela 2026-09-30, A] Z kodem typu (składany wniosek, korekta) picker działa
                    w TRYBIE WNIOSKU: kandydaci = cały dział osoby wniosku (z komórkami) z serwera, bez wpisu
                    ręcznego; „Bez osoby zastępującej” tylko przy polach zastępstwa. Bez kodu typu (wydawanie
                    dokumentów, offboarding) — zachowanie jak dotąd. Pole o szerszym zakresie (pickerScope
                    company / outsideDepartment) nie obiecuje w placeholderze „z działu”. */}
                <DepartmentMemberPicker
                    value={value || null}
                    onChange={(val) => handleChange(field.name, val)}
                    excludeUserId={user?.id}
                    placeholder={!options.typeCode ? "Wybierz pracownika z działu..."
                        : (!field.pickerScope || field.pickerScope === "department" ? "Wybierz osobę z działu..." : "Wybierz osobę...")}
                    disabled={readOnly}
                    hrTypeCode={options.typeCode || undefined}
                    fieldName={field.name}
                    forUserId={options.forUserId || undefined}
                    requestId={options.requestId || undefined}
                    pozwolBrak={czyPoleZastepstwa(field)}
                />
                {error && <span className={fieldStyles.errorText}>{error}</span>}
            </div>
        );
    }

    // Default: TEXT / IBAN / fallback
    return (
        <div key={field.name} className={fieldStyles.fieldGroup}>
            <label className={fieldStyles.fieldLabel}>
                {field.label}
                {field.required && <span className={fieldStyles.fieldRequired}>*</span>}
            </label>
            {field.description && <small className={fieldStyles.fieldHint}>{field.description}</small>}
            <input
                type="text"
                className={`${fieldStyles.fieldInput} ${error ? fieldStyles.fieldError : ""}`}
                value={value || ""}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={
                    field.placeholder ||
                    (field.type === FIELD_TYPE.IBAN ? "PL 00 0000 0000 0000 0000 0000 0000" : "")
                }
                readOnly={readOnly}
            />
            {error && <span className={fieldStyles.errorText}>{error}</span>}
        </div>
    );
}

// ── Render all form fields ───────────────────────────────────────
export function renderAllFormFields(fields, formData, onChange, options = {}) {
    if (!fields || fields.length === 0) return null;
    return fields.map((field) => renderFormField(field, formData, onChange, options));
}
